import React from 'react';
import { Collapse as AntCollapse, CollapseProps as AntCollapseProps, CollapsePanelProps as AntCollapsePanelProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface CollapseProps extends AntCollapseProps {
  className?: string;
}

export interface CollapsePanelProps extends AntCollapsePanelProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  collapse: {
    '&.ant-collapse': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      borderRadius: theme.radius.sm,
    },
    '& .ant-collapse-item': {
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    '& .ant-collapse-header': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      color: theme.color.semantic.text.primary,
      padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
        color: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-collapse-item:first-child > .ant-collapse-header': {
      borderTopLeftRadius: theme.radius.sm,
      borderTopRightRadius: theme.radius.sm,
    },
    '& .ant-collapse-item:last-child > .ant-collapse-header': {
      borderBottomLeftRadius: theme.radius.sm,
      borderBottomRightRadius: theme.radius.sm,
    },
    '& .ant-collapse-item-active:last-child > .ant-collapse-header': {
      borderBottomLeftRadius: theme.radius.none,
      borderBottomRightRadius: theme.radius.none,
    },
    '& .ant-collapse-item-active .ant-collapse-header': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
    },
    '& .ant-collapse-content': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
    },
    '& .ant-collapse-content-box': {
      padding: theme.spacing[4],
      fontSize: theme.typography.fontSize.sm,
      color: theme.color.semantic.text.primary,
    },
    '& .ant-collapse-item-disabled .ant-collapse-header': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      color: theme.color.semantic.text.soft,
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
      },
    },
    '& .ant-collapse-expand-icon': {
      color: theme.color.semantic.text.secondary,
      '&:hover': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
  },
  collapseBorderless: {
    '&.ant-collapse-borderless': {
      backgroundColor: 'transparent',
      border: 'none',
    },
    '&.ant-collapse-borderless .ant-collapse-item': {
      borderBottom: 'none',
    },
  },
  collapseGhost: {
    '&.ant-collapse-ghost': {
      backgroundColor: 'transparent',
      border: 'none',
    },
    '&.ant-collapse-ghost .ant-collapse-item': {
      borderBottom: 'none',
    },
    '&.ant-collapse-ghost .ant-collapse-header': {
      backgroundColor: 'transparent',
    },
    '&.ant-collapse-ghost .ant-collapse-content': {
      backgroundColor: 'transparent',
      borderTop: 'none',
    },
  },
  collapseAccordion: {
    // Accordion mode styles (handled by antd prop)
  },
}));

const CollapseInner: React.FC<CollapseProps> = ({
  bordered = true,
  ghost = false,
  accordion = false,
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const getVariantClass = () => {
    if (ghost) return classes.collapseGhost;
    if (!bordered) return classes.collapseBorderless;
    return '';
  };

  const combinedClassName = `${classes.collapse} ${getVariantClass()} ${accordion ? classes.collapseAccordion : ''} ${className}`.trim();

  return (
    <AntCollapse
      bordered={bordered}
      ghost={ghost}
      accordion={accordion}
      {...rest}
      className={combinedClassName}
    />
  );
};

// Create Collapse with Panel property
export const Collapse = Object.assign(CollapseInner, {
  Panel: AntCollapse.Panel,
});

export default Collapse;
